import React, { Fragment } from "react";
import { observer, inject } from "mobx-react";
import { autorun } from "mobx";
import { isEqual } from "lodash/lang";
import DataGrid from "../../../dataGrid";
import GridConfig from "./gridConfig";
import { withUserProfile } from "../../../../userProfile/userProfileContext";
import { getDataWithQueryString } from "../../../../api/componentAPIs";
import { compose } from "recompose";
import { withSnackbar } from "notistack";
import StatusBar from "../../../dataGrid/StatusBar";
import GeocodingModal from "../../Common/Geocoding/GeoCodingModal";
import { getCustomerManagementData } from "../../Common/Geocoding/utils";
import Events from "../../../../utilities/EventEmitter";
import { getFilteredData } from "../../../common/filterUtilities";
import appConstants from "../../../../appConstants";
import { getSpecialNeeds } from "../../utils";

@inject("IBPlanningStore")
@observer
class Customers extends React.Component {
  constructor(props) {
    super(props);
    this.gridConfig = new GridConfig();
    this.state = {
      quickFilterText: "",
      geoCodeCustomer: null,
      customerData: null,
      terminalId: this.props.userData.userProfile.terminal.id,
      quickFilter: null,
    };
    this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
    this.gridContext = {
      specialNeedsOptions: getSpecialNeeds(this.serviceLevelList),
    };
    this.gridHeaderId = `gridHeader_${this.props.glContainer._config.id}`;
    this.selectedCustomers = [];
  }

  componentDidMount() {
    this._isMounted = true;
    this.props.glEventHub.on(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.on(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.on("updateCustomerData", this.updateCustomerData);
    this.props.glEventHub.on(
      "updateQuickFilter",
      this.updateCustomerQuickFilter
    );
    this.props.glEventHub.on(
      `activeTabSwitched_${appConstants.gridNames.CustomerGrid}`,
      this.onSelectionChanged
    );
    this.getCustomerData();
    this.autorunPriorityShipmentList = autorun(() => {
      if (
        !isEqual(
          this.serviceLevelList,
          this.props.IBPlanningStore.serviceLevelList
        )
      ) {
        this.serviceLevelList = this.props.IBPlanningStore.serviceLevelList;
        this.gridContext = {
          specialNeedsOptions: getSpecialNeeds(this.serviceLevelList),
        };
      }
    });
  }

  componentWillUnmount() {
    this._isMounted = false;
    this.props.glEventHub.off(this.gridHeaderId, this.onSearchTextChanged);
    this.props.glEventHub.off(
      `${this.gridHeaderId}_geocode`,
      this.onGeocodeCustomer
    );
    Events.off("updateCustomerData", this.updateCustomerData);
    this.props.glEventHub.off(
      "updateQuickFilter",
      this.updateCustomerQuickFilter
    );
    this.props.glEventHub.off(
      `activeTabSwitched_${appConstants.gridNames.CustomerGrid}`,
      this.onSelectionChanged
    );
    this.autorunPriorityShipmentList?.();
  }

  updateCustomerQuickFilter = (quickFilterData) => {
    if (
      quickFilterData.filterName ===
      appConstants.CUSTOMER_MANAGEMENT_QUICKFILTER_NAMES.CUSTOMER_GRID
    ) {
      this.setState({
        quickFilter: quickFilterData.eventData,
      });
    }
  };

  static getDerivedStateFromProps(nextProps, state) {
    if (nextProps.userData.userProfile.terminal.id !== state.terminalId) {
      return {
        terminalId: nextProps.userData.userProfile.terminal.id,
        customerData: null,
      };
    }
    return null;
  }

  componentDidUpdate(prevProps, prevState) {
    if (
      prevProps.userData.userProfile.terminal.id !==
      this.props.userData.userProfile.terminal.id
    ) {
      this.getCustomerData();
    }
  }

  getCustomerData = () => {
    this.setState({
      customerData: null,
    });

    getDataWithQueryString(
      "GetCustomerDetail",
      `terminalId=${this.props.userData.userProfile.terminal.id}`
    ).then((response) => {
      if (!this._isMounted) {
        return;
      }
      if (response.status === 200) {
        this.setState(
          {
            customerData: response?.data ?? [],
          },
          this.updateQuickFilterData
        );
      } else {
        this.setState(
          {
            customerData: [],
          },
          this.updateQuickFilterData
        );
      }
    });
  };

  updateCustomerData = (customerCodes) => {
    getDataWithQueryString(
      "GetCustomerDetail",
      `terminalId=${this.props.userData.userProfile.terminal.id}&customerCodes=${customerCodes}`
    ).then((response) => {
      if (response.status === 200) {
        const updatedCustomer = response?.data ?? [];
        const customerData = this.state.customerData;
        updatedCustomer.map((customer) => {
          const index = customerData.findIndex((c) => c.code === customer.code);
          customerData[index] = customer;
        });
        Events.emit("updateShipper", updatedCustomer); // Check for global update
        this.setState(
          {
            customers: customerData,
          },
          this.updateQuickFilterData
        );
      }
    });
  };

  updateQuickFilterData = () => {
    let [maxEquipSizes, overrideRoutes] = [[], []];
    this.state.customerData.forEach((customer) => {
      const { maxEquipmentSize, route } = customer.deliveryDetails;
      if (maxEquipmentSize && !maxEquipSizes.includes(maxEquipmentSize)) {
        maxEquipSizes.push(maxEquipmentSize);
      }
      if (route && !overrideRoutes.includes(route)) {
        overrideRoutes.push(route);
      }
    });
    this.props.glEventHub.emit("updatedCustomerData", {
      maxEquipSizes,
      overrideRoutes,
    });
  };

  onGeocodeCustomer = () => {
    const selectedCustomers = this.gridApi.getSelectedRows();
    if (!selectedCustomers.length) {
      return;
    }

    this.setState({
      geoCodeCustomer: {
        ...getCustomerManagementData(selectedCustomers[0]),
        proId: "",
      },
    });
  };

  onSearchTextChanged = (searchText) => {
    this.setState({
      quickFilterText: searchText,
    });
  };

  onGridReady = ({ api }) => {
    this.gridApi = api;
    this.gridApi.runCommand = this.runCommand;
  };

  runCommand = (command, data) => {
    if (command === "openGeoCodeModel") {
      this.setState({
        geoCodeCustomer: getCustomerManagementData(data),
      });
    }
  };

  onSelectionChanged = () => {
    if(this.props.glContainer.tab.isActive){
    this.selectedCustomers = this.gridApi.getSelectedRows();
    this.props.glEventHub.emit("customerSelectedonGrid", {
      selectedCustomers: this.selectedCustomers,
      stopType: appConstants.ROUTE_STOP_TYPES.DL,
    });
  }
  };

  closeGeocodeModal = (hasDataChanged = false) => {
    if (hasDataChanged) {
      this.updateCustomerData(this.state.geoCodeCustomer.code);
    }
    this.setState({
      geoCodeCustomer: null,
    });
  };

  onDoubleClicked = (params) => {
    Events.emit("OpenEditCustomerDialog", params?.node?.data.code);
  };

  constructStatusBar = (customerList) => {
    return [
      {
        label: `${customerList.length} Customers`,
      },
    ];
  };

  onRowDataChanged = () => {
    if (!this.state.customerData) {
      this.gridApi?.hideOverlay();
      this.gridApi?.showLoadingOverlay();
    } else {
      this.gridApi?.hideOverlay();
    }
    const selectedCustomerCodes = this.selectedCustomers.map(
      (customer) => customer.code
    );
    this.gridApi.forEachNode?.((node) => {
      if (selectedCustomerCodes.includes(node.data.code)) {
        node.setSelected(true, false, true); // props are isSelected, clearOtherSelection, suppressFinishActions
      } else {
        // disabling supress finish actions calls onSelectionChanged event for each selection
        node.setSelected(false, false, true); // but we want single call of onSelectionChanged for multiselection , behaviour observed through user action
      } // so we suppress the call from setSelected and call it explicitly after loop completes
    });
    this.gridApi.dispatchEvent({
      type: "selectionChanged",
      api: this.gridApi,
    });
  };

  render() {
    const customerData = this.state.quickFilter
      ? getFilteredData(
          this.state.customerData ?? [],
          this.state.quickFilter,
          new Date()
        )
      : this.state.customerData?.slice();
    return (
      <Fragment>
        <DataGrid
          key={this.props.userData.userProfile.terminal.id}
          defaultColDef={this.gridConfig.config.defaultColDef}
          columnDefs={this.gridConfig.config.columns}
          columnTypes={this.gridConfig.config.columnTypes}
          config={this.gridConfig.config}
          rowSelection={this.gridConfig.config.rowSelection}
          rowDeselection={this.gridConfig.config.rowDeselection}
          enableFilter={this.gridConfig.config.enableFilter}
          enableSorting={this.gridConfig.config.enableSorting}
          rowData={customerData}
          onRowDataChanged={this.onRowDataChanged}
          onGridReady={this.onGridReady}
          onSelectionChanged={this.onSelectionChanged}
          layoutContainer={this.props.glContainer}
          quickFilterText={this.state.quickFilterText}
          onCellDoubleClicked={this.onDoubleClicked}
          panelLabel={(filteredData) => (
            <StatusBar
              data={this.constructStatusBar(filteredData)}
              filterGridData={this.updateGridData}
            />
          )}
          context={this.gridContext}
        />
        {this.state.geoCodeCustomer && (
          <GeocodingModal
            customer={this.state.geoCodeCustomer}
            userData={this.props.userData.userProfile}
            close={this.closeGeocodeModal}
          />
        )}
      </Fragment>
    );
  }
}

export default compose(withUserProfile, withSnackbar)(Customers);
